
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import StoriesCard from './StoriesCard.vue'
import { useStoriesStore } from '../../store/stores/stories/stories'
import useRouter from '../../hooks/useRouter'
import config from '@root/config'
import { filterUninstalledPromos } from '@/api/marketplace'

const PROMO_CARDS = [
    {
        id: 'promo-max-business',
        kind: 'promo',
        shellClass: 'dh-ad-shell--diagnostic',
        cardClass: 'dh-ad-card--diagnostic',
        btnClass: 'dh-ad-cta',
        titleLines: ['Диагностика'],
        lead: 'Потенциал базы за 1 день',
        desc: 'Разберём базу, посчитаем потенциал повторных визитов и покажем, где салон недозарабатывает.',
        btnText: 'Получить бесплатно →',
        visual: 'diagnostic',
        marketplaceId: config.marketplaceModulesIds[config.buildType].diagnostics,
    },
    {
        id: 'promo-crm',
        kind: 'promo',
        shellClass: 'dh-ad-shell--crm',
        cardClass: 'dh-ad-card--small',
        dotsClass: 'dh-ad-dots',
        btnClass: 'dh-ad-cta dh-ad-cta--outline dh-ad-cta--green',
        titleLines: ['CRM-маркетинг', 'под ключ'],
        desc: 'Полная настройка системы по работе с клиентской базой.',
        btnText: 'Подробнее',
        visual: 'crm',
        marketplaceId: config.marketplaceModulesIds[config.buildType].crmSettings,
    },
    {
        id: 'promo-intensive',
        kind: 'promo',
        shellClass: 'dh-ad-shell--sales',
        cardClass: 'dh-ad-card--small',
        dotsClass: 'dh-ad-dots dh-ad-dots--teal',
        btnClass: 'dh-ad-cta dh-ad-cta--outline dh-ad-cta--teal',
        titleLines: ['Интенсив', 'по продажам'],
        desc: 'Отработка возражений и техники продаж для администраторов.',
        btnText: 'Подробнее',
        visual: 'sales',
        marketplaceId: config.marketplaceModulesIds[config.buildType].salesTraining,
    },
] as const

const STACK_BREAKPOINT = 720
const ROW_CARD_HEIGHT = 292
const STACK_CARD_HEIGHT = 220
const SLIDE_GAP = 12

export default defineComponent({
    props: {},
    components: { StoriesCard },
    setup(props, { emit }) {
        const root = getCurrentInstance()!.proxy

        const { route, router } = useRouter(root)
        const storiesStore = useStoriesStore()
        const projectId = computed(() => route.value.params.id)
        const loading = ref(true)
        const visiblePromoCards = ref<(typeof PROMO_CARDS)[number][]>([])
        const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1280)
        const isStacked = computed(() => windowWidth.value <= STACK_BREAKPOINT)

        const updateWindowWidth = () => {
            windowWidth.value = window.innerWidth
        }

        const carouselHeight = computed(() => {
            if (isStacked.value) {
                return STACK_CARD_HEIGHT * 3 + SLIDE_GAP * 2 + 16
            }
            return ROW_CARD_HEIGHT + 16
        })

        const filterInstalledPromoCards = async (currentProjectId: string) => {
            visiblePromoCards.value = await filterUninstalledPromos(currentProjectId, PROMO_CARDS)
        }

        const setVideoHelepVisible = (value) => {
            emit('show-video', value)
        }
        const goToMarketplaceModule = (marketplaceId) => {
            if (!marketplaceId) return

            const targetPath = projectId.value
                ? `/project/${projectId.value}/marketplace/integration/${marketplaceId}`
                : `/marketplace/integration/${marketplaceId}`

            const { href } = router.resolve(targetPath)
            window.open(href, '_blank', 'noopener,noreferrer')
        }

        const stories = computed(() => {
            if (loading.value) return [{ id: '1' }, { id: '2' }, { id: '3' }]
            return [...visiblePromoCards.value, ...storiesStore.getProjectStories(projectId.value)]
        })
        const currentSlide = ref(0)

        const processedSlides = computed(() => {
            if (loading.value) return [[{}, {}, {}]]

            const slides = []
            const total = stories.value.length

            for (let i = -1; i < total; i++) {
                const slideItems = []
                for (let j = 0; j < 3; j++) {
                    const index = (i + j) % total
                    slideItems.push(stories.value[index >= 0 ? index : total + index])
                }
                if (i >= 0) slides.push(slideItems)
            }

            return slides.length > 0 ? slides : [[{}, {}, {}]]
        })
        onMounted(async () => {
            updateWindowWidth()
            window.addEventListener('resize', updateWindowWidth)
            loading.value = true
            await Promise.all([
                storiesStore.fetchProjectStories(projectId.value),
                filterInstalledPromoCards(projectId.value),
            ])
            loading.value = false
        })
        onBeforeUnmount(() => {
            window.removeEventListener('resize', updateWindowWidth)
        })
        return {
            stories,
            loading,
            setVideoHelepVisible,
            goToMarketplaceModule,
            currentSlide,
            processedSlides,
            carouselHeight,
            isStacked,
        }
    },
})
